import { FC, useState, useMemo, useEffect } from 'react'
import { formatInTimeZone } from 'date-fns-tz'

import RFFFields from '../../react-final-form-fields'
import config from '../../../../config'
import { useAppContext } from '../../../../app-context'

import { validateRequired, validateMaxSymbols } from '../../../../util/validation'
import { nameofFactory, getPlaceholderTextFromLabel, toOptions } from '../../../../util'

import { ISafetyCaseActionFormFieldsProps, ISafetyCaseActionValues } from './safety-case-action-form-types'
import { useSafetyCaseActionTypesQuery } from '../../../../services'

const validateMaxSymbols500 = validateMaxSymbols(500, 'Description must not exceed 500 characters')

const nameof = nameofFactory<ISafetyCaseActionValues>()

const actionLabel = 'Action Taken'
const notesLabel = 'Notes'

const SafetyCaseActionFormFields: FC<ISafetyCaseActionFormFieldsProps> = props => {
  const { disabled, form, initialValues } = props
  const { state: appState } = useAppContext()

  const { data: safetyCaseActionTypes, isFetching: isSafetyCaseActionTypesLoading } = useSafetyCaseActionTypesQuery({
    activeOnly: true,
  })

  const initialSafetyCaseActions = useMemo(() => {
    const baseOptions = toOptions(safetyCaseActionTypes)
    const customLabel = initialValues?.actionTypeText
    if (customLabel && !baseOptions.some(option => option.label === customLabel)) {
      baseOptions.unshift({ label: customLabel, value: customLabel })
    }
    return baseOptions
  }, [safetyCaseActionTypes, initialValues?.actionTypeText])

  const [actionOptions, setActionOptions] = useState(initialSafetyCaseActions)

  useEffect(() => {
    setActionOptions(initialSafetyCaseActions)
  }, [initialSafetyCaseActions])

  return (
    <>
      <RFFFields.Select
        name={nameof('actionTypeId')}
        label={actionLabel}
        placeholder="Select from default actions or begin typing to provide your own response"
        disabled={disabled || isSafetyCaseActionTypesLoading}
        options={actionOptions}
        validate={validateRequired}
        onCreateOption={(optionLabel: string) => {
          setActionOptions(actionOptions => [{ label: optionLabel, value: optionLabel }, ...actionOptions])
          form?.mutators?.onActivityChange?.(optionLabel)
        }}
        canCreate={true}
        isClearable={true}
      />

      <RFFFields.DatePicker
        name={nameof('occurrenceDate')}
        label="Occurrence Date"
        placeholder="Select the date of the action"
        disabled={disabled}
        validate={validateRequired}
        initialValue={
          initialValues?.occurrenceDate ||
          formatInTimeZone(new Date(), appState.airport.timeZoneIanaId, config.dateAndTimeFormats.dateAndTime)
        }
      />

      <RFFFields.Textarea
        name={nameof('notes')}
        label={notesLabel}
        placeholder={getPlaceholderTextFromLabel(notesLabel)}
        disabled={disabled}
        validate={validateMaxSymbols500}
      />

      <RFFFields.DocumentUpload name={nameof('documents')} parentType="SafetyCaseAction" disabled={disabled} />
    </>
  )
}

export default SafetyCaseActionFormFields
